import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  getPlayers,
  getPreferredTables,
  putPreferredTables,
} from '@/lib/api';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import type { Player } from '@swiss-magic/shared-types';
import { useHubOutletContext } from './TournamentHubLayoutRoute';

// Story 106 — preferred table numbers. A priority-ordered list of
// (player, physical table) records; the topmost record has the highest
// priority when pairings are generated. Local editing + explicit Save
// (full-list PUT — idempotent, matches the ordered-list model).

interface EditableRecord {
  playerId: string;
  /** Kept as string while editing; validated on save. */
  tableNumber: string;
}

export function PreferredTablesPage() {
  const { t } = useTranslation('director');
  const { tournament } = useHubOutletContext();
  const tournamentId = tournament?.id;

  const [players, setPlayers] = useState<Player[]>([]);
  const [records, setRecords] = useState<EditableRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tournamentId) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ players: allPlayers }, { records: existing }] =
          await Promise.all([
            getPlayers(tournamentId),
            getPreferredTables(tournamentId),
          ]);
        if (cancelled) return;
        // Same player set as the starting-list report: everyone who started
        // in the tournament. Withdrawn/eliminated players stay listed so
        // their saved records keep rendering; pre-start, spot-secured
        // players are part of the visible field (story 100).
        const preStart =
          tournament?.status === 'registration_open' ||
          tournament?.status === 'registration_closed';
        const startedStatuses = preStart
          ? ['accepted', 'spot_secured', 'withdrawn', 'eliminated']
          : ['accepted', 'withdrawn', 'eliminated'];
        setPlayers(allPlayers.filter((p) => startedStatuses.includes(p.status)));
        setRecords(
          existing.map((r) => ({
            playerId: r.playerId,
            tableNumber: String(r.tableNumber),
          })),
        );
      } catch {
        if (!cancelled) showErrorToast(t('preferred_tables.load_failed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, tournament?.status, t]);

  function updateRecord(index: number, patch: Partial<EditableRecord>) {
    setRecords((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  }

  function moveUp(index: number) {
    if (index === 0) return;
    setRecords((prev) => {
      const next = prev.slice();
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  }

  function moveDown(index: number) {
    setRecords((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = prev.slice();
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  }

  function removeRecord(index: number) {
    setRecords((prev) => prev.filter((_, i) => i !== index));
  }

  function addRecord() {
    // New rows start blank — the director explicitly picks the player.
    setRecords((prev) => [...prev, { playerId: '', tableNumber: '' }]);
  }

  async function save() {
    if (!tournamentId) return;
    const payload = [];
    for (const r of records) {
      const table = Number(r.tableNumber);
      if (!r.playerId || !Number.isInteger(table) || table < 1 || table > 999) {
        showErrorToast(t('preferred_tables.validation_failed'));
        return;
      }
      payload.push({ playerId: r.playerId, tableNumber: table });
    }
    if (new Set(payload.map((r) => r.playerId)).size !== payload.length) {
      showErrorToast(t('preferred_tables.duplicate_player'));
      return;
    }
    setSaving(true);
    try {
      const { records: saved } = await putPreferredTables(tournamentId, {
        records: payload,
      });
      setRecords(
        saved.map((r) => ({
          playerId: r.playerId,
          tableNumber: String(r.tableNumber),
        })),
      );
      showSuccessToast(t('preferred_tables.saved'));
    } catch {
      showErrorToast(t('preferred_tables.save_failed'));
    } finally {
      setSaving(false);
    }
  }

  if (!tournament || loading) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('tournament_hub.loading')}
      </div>
    );
  }

  return (
    <div data-testid="preferred-tables-page" className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">
          {t('preferred_tables.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('preferred_tables.description')}
        </p>
      </div>

      <Card className="p-4">
        {records.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('preferred_tables.empty')}
          </p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_7rem_auto] items-center gap-2 text-xs font-medium text-muted-foreground">
              <span>{t('preferred_tables.player_column')}</span>
              <span>{t('preferred_tables.table_column')}</span>
              <span />
            </div>
            {records.map((r, i) => (
              <div
                key={i}
                data-testid={`preferred-table-row-${i}`}
                className="grid grid-cols-[1fr_7rem_auto] items-center gap-2"
              >
                <select
                  data-testid={`preferred-player-select-${i}`}
                  value={r.playerId}
                  onChange={(e) => updateRecord(i, { playerId: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-input/30"
                >
                  <option value="" disabled>
                    {t('preferred_tables.pick_player')}
                  </option>
                  {players.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </option>
                  ))}
                </select>
                <Input
                  data-testid={`preferred-table-input-${i}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={r.tableNumber}
                  onChange={(e) =>
                    updateRecord(i, {
                      tableNumber: e.target.value.replace(/[^0-9]/g, ''),
                    })
                  }
                  placeholder={t('preferred_tables.table_placeholder')}
                />
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid={`preferred-move-up-${i}`}
                    aria-label={t('preferred_tables.move_up')}
                    disabled={i === 0}
                    onClick={() => moveUp(i)}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid={`preferred-move-down-${i}`}
                    aria-label={t('preferred_tables.move_down')}
                    disabled={i === records.length - 1}
                    onClick={() => moveDown(i)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid={`preferred-delete-${i}`}
                    aria-label={t('preferred_tables.delete')}
                    onClick={() => removeRecord(i)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="add-preferred-table"
            onClick={addRecord}
            disabled={players.length === 0}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('preferred_tables.add')}
          </Button>
          <Button
            data-testid="save-preferred-tables"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving
              ? t('preferred_tables.saving')
              : t('preferred_tables.save')}
          </Button>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">
        {t('preferred_tables.priority_hint')}
      </p>
    </div>
  );
}
